import { ICompositionEntity } from '@maia/shared';
import {
  IGetCartEntity,
  IPostCartEntity,
  IPreconditionQuestion,
} from '../entitites/cart.entity';
import { AnswerDto } from './question/question.dto';

export class CartDto {
  readonly cartGuid: string;
  readonly errorCode: string | undefined;

  constructor(entity: IPostCartEntity) {
    this.cartGuid = entity.cart_guid;
    this.errorCode = entity.error_code ?? undefined;
  }
}

export class GetCartDto {
  readonly guid: string;
  readonly specialLimit: boolean;

  constructor(entity: IGetCartEntity) {
    this.guid = entity.guid;
    this.specialLimit = entity.special_limit;
  }
}

export class CreateCartDto {
  readonly sales_channel: string;
  readonly selected_products: Array<{
    guid: string;
    object_reference: string | null;
    risk_pool: string | null;
  }>;

  constructor(params: {
    salesChannel: string;
    selectedProducts: Array<{
      guid: string;
      objectReference: string | null;
      riskPool: string | null;
    }>;
  }) {
    this.sales_channel = params.salesChannel;
    this.selected_products = params.selectedProducts.map(
      ({ guid, objectReference, riskPool }) => ({
        guid,
        object_reference: objectReference ?? null,
        risk_pool: riskPool,
      }),
    );
  }
}

export class ProconditionQuestionDto {
  readonly guid: string;
  readonly reference: string;
  readonly title: string;
  readonly phrasing: string;
  readonly icon: string;
  readonly answer: AnswerDto;
  readonly revalidateOnChange: boolean;
  options:
    | Array<{
        readonly value: string;
        readonly label: string;
      }>
    | [];
  replacements:
    | Array<{
        readonly replace_this: string;
        readonly replace_with: string;
      }>
    | [];
  readonly isAnswered: boolean;
  readonly inputType:
    | 'toggle_group'
    | 'text'
    | 'radio'
    | 'switch'
    | 'number'
    | 'money'
    | 'date'
    | null;
  readonly moreInformation: null | string;

  constructor(params: IPreconditionQuestion) {
    this.guid = params.guid;
    this.reference = params.reference;
    this.title = params.title;
    this.phrasing = params.phrasing;
    this.icon = params.icon;
    this.answer = new AnswerDto(params.answer);
    this.options = params.options;
    this.revalidateOnChange = params.revalidate_on_change;
    this.replacements = params.replacements;
    this.isAnswered = params.is_answered;
    this.inputType = params.input_type;
    this.moreInformation = params.more_information;
  }
}

export class CompositionsDto {
  readonly sequence: number;
  readonly guid: string;
  readonly code: string;
  readonly title: string;
  readonly description: string;
  readonly minimumPrice: string;
  readonly primaryOptions: Array<{
    readonly optionName: string;
    readonly optionCode: string;
    readonly icon: string;
  }>;
  readonly addons: Array<{
    readonly optionName: string;
    readonly optionCode: string;
    readonly icon: string;
  }>;
  readonly insuredActivities: Array<{
    readonly sequence: number;
    readonly guid: string;
    readonly icon: string;
    readonly description: string;
  }>;

  constructor(params: ICompositionEntity) {
    this.sequence = params.sequence;
    this.guid = params.guid;
    this.code = params.code;
    this.title = params.product_name;
    this.description = params.description;
    this.minimumPrice = params.minimum_price;
    this.primaryOptions = params.primary_options.map((o) => ({
      optionName: o.option_name,
      optionCode: o.option_code,
      icon: o.icon,
    }));
    this.addons = params.addons.map((o) => ({
      optionName: o.option_name,
      optionCode: o.option_code,
      icon: o.icon,
    }));
    this.insuredActivities = params.insured_activities.map((a) => ({
      sequence: a.sequence,
      guid: a.guid,
      icon: a.icon,
      description: a.description,
    }));
  }
}
